import { useState } from "react";
import { Upload, PenLine, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import CompanySearch from "@/components/companies/CompanySearch";
import { addCompany } from "@/lib/dataService";
import { getActiveRegionConfig } from "@/lib/regionConfig";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatYearEnd(accountingRef) {
  if (!accountingRef?.month) return "";
  const month = parseInt(accountingRef.month, 10);
  return `${accountingRef.day} ${MONTHS[month - 1] || ""}`;
}

const COMPANY_TYPES = {
  ltd: "Private Limited Company",
  plc: "Public Limited Company",
  llp: "Limited Liability Partnership",
  unlimited: "Unlimited Company",
};

const COMPANY_TYPE_OPTIONS = [
  "Private Limited Company",
  "Public Limited Company",
  "Limited Liability Partnership",
  "Unlimited Company",
  "Overseas Company",
  "Other",
];

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

/**
 * Add-company form (Companies House search, manual entry, bulk upload) used inline above a table
 * and on the standalone /companies/add page. The parent decides what happens after save or cancel.
 */
const AddCompanyPanel = ({ onSaved, onCancel, initialTab, className = "" }) => {
  const regionConfig = getActiveRegionConfig();
  const supportsLiveCompanySearch = regionConfig.code === "UK";

  const [activeTab, setActiveTab] = useState(
    initialTab || (supportsLiveCompanySearch ? "search" : "manual"),
  );
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [saving, setSaving] = useState(false);

  // Manual entry form state
  const [manual, setManual] = useState({
    name: "",
    registrationNumber: "",
    companyType: "",
    registeredAddress: "",
    status: "Active",
    yearEndDay: "",
    yearEndMonth: "",
    sicCodes: "",
  });

  const handleManualChange = (field, value) => {
    setManual((prev) => ({ ...prev, [field]: value }));
  };

  const handleCompanySelect = (profile) => {
    setSelectedCompany(profile);
    toast.success("Company data fetched from Companies House");
  };

  const handleSwitchToManual = () => {
    setActiveTab("manual");
  };

  const handleSaveSearch = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company first");
      return;
    }
    const company = {
      name: selectedCompany.company_name,
      registrationNumber: selectedCompany.company_number,
      registeredAddress: selectedCompany.address_formatted || "",
      companyType:
        COMPANY_TYPES[selectedCompany.company_type] ||
        selectedCompany.company_type ||
        "",
      status: selectedCompany.company_status
        ? selectedCompany.company_status.charAt(0).toUpperCase() +
          selectedCompany.company_status.slice(1)
        : "",
      yearEnd: formatYearEnd(
        selectedCompany.accounts?.accounting_reference_date,
      ),
      accountsDue: selectedCompany.accounts?.next_due || "",
      confirmationDue: selectedCompany.confirmation_statement?.next_due || "",
      sicCodes: selectedCompany.sic_codes || [],
    };
    await saveCompany(company);
  };

  const handleSaveManual = async () => {
    if (!manual.name.trim()) {
      toast.error("Company name is required");
      return;
    }
    const yearEnd =
      manual.yearEndDay && manual.yearEndMonth
        ? `${manual.yearEndDay} ${MONTHS[parseInt(manual.yearEndMonth, 10) - 1]}`
        : "";
    const company = {
      name: manual.name.trim(),
      registrationNumber: manual.registrationNumber.trim(),
      registeredAddress: manual.registeredAddress.trim(),
      companyType: manual.companyType,
      status: manual.status,
      yearEnd,
      accountsDue: "",
      confirmationDue: "",
      sicCodes: manual.sicCodes
        ? manual.sicCodes
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [],
    };
    await saveCompany(company);
  };

  const saveCompany = async (company) => {
    setSaving(true);
    try {
      const created = await addCompany(company);
      toast.success("Company added successfully");
      onSaved?.(created);
    } catch (err) {
      toast.error(err?.message || "Failed to save company. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => onCancel?.();

  const handleBulkUpload = (e) => {
    if (e.target.files[0]) toast.info("Bulk upload feature coming soon");
  };

  return (
    <div
      className={`bg-card border border-border rounded-md p-6 ${className}`}
      data-testid="add-company-panel"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="font-heading text-lg font-medium">Add Company</h2>
          <p className="text-sm text-muted-foreground">
            Add company details for {regionConfig.code}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          aria-label="Close"
          data-testid="add-company-close"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            {supportsLiveCompanySearch && (
              <TabsTrigger value="search">
                Search {regionConfig.searchProviderLabel}
              </TabsTrigger>
            )}
            <TabsTrigger value="manual">Manual Entry</TabsTrigger>
            <TabsTrigger value="bulk">Bulk Upload</TabsTrigger>
          </TabsList>

          {/* ── Search Companies House ── */}
          {supportsLiveCompanySearch && (
            <TabsContent value="search" className="space-y-6">
              <div>
                <Label className="mb-2 block">
                  Company Name or {regionConfig.companyIdLabel}
                </Label>
                <CompanySearch
                  onSelect={handleCompanySelect}
                  onManualEntry={handleSwitchToManual}
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Results fetched live from Companies House
                </p>
              </div>

              {selectedCompany && (
                <div
                  className="border border-border rounded-md p-4 bg-muted/50"
                  data-testid="fetched-data"
                >
                  <h3 className="font-medium mb-4">Company Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">
                        Company Name
                      </p>
                      <p className="font-medium">
                        {selectedCompany.company_name}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">
                        {regionConfig.companyIdLabel}
                      </p>
                      <p className="font-mono">
                        {selectedCompany.company_number}
                      </p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-muted-foreground text-xs mb-1">
                        Registered Address
                      </p>
                      <p>{selectedCompany.address_formatted}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">
                        Year End
                      </p>
                      <p>
                        {formatYearEnd(
                          selectedCompany.accounts?.accounting_reference_date,
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">
                        Accounts Due
                      </p>
                      <p className="font-medium">
                        {selectedCompany.accounts?.next_due}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">
                        Status
                      </p>
                      <p
                        className={
                          selectedCompany.company_status === "active"
                            ? "text-green-600 font-medium"
                            : "text-orange-500 font-medium"
                        }
                      >
                        {selectedCompany.company_status
                          ?.charAt(0)
                          .toUpperCase()}
                        {selectedCompany.company_status?.slice(1)}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs mb-1">
                        Confirmation Due
                      </p>
                      <p>{selectedCompany.confirmation_statement?.next_due}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveSearch}
                  disabled={!selectedCompany || saving}
                  data-testid="save-btn"
                >
                  {saving ? "Saving..." : "Save Company"}
                </Button>
              </div>
            </TabsContent>
          )}

          {/* ── Manual Entry ── */}
          <TabsContent value="manual" className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label className="mb-2 block">
                  Company Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={manual.name}
                  onChange={(e) => handleManualChange("name", e.target.value)}
                  placeholder="Enter company name"
                  data-testid="manual-name"
                />
              </div>

              <div>
                <Label className="mb-2 block">
                  {regionConfig.companyIdLabel}
                </Label>
                <Input
                  value={manual.registrationNumber}
                  onChange={(e) =>
                    handleManualChange("registrationNumber", e.target.value)
                  }
                  placeholder={
                    regionConfig.code === "SG"
                      ? "e.g. 202012345A"
                      : "e.g. 12345678"
                  }
                  data-testid="manual-reg-number"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional when unavailable
                </p>
              </div>

              <div>
                <Label className="mb-2 block">Company Type</Label>
                <Select
                  value={manual.companyType}
                  onValueChange={(v) => handleManualChange("companyType", v)}
                >
                  <SelectTrigger data-testid="manual-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_TYPE_OPTIONS.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="col-span-2">
                <Label className="mb-2 block">Registered Address</Label>
                <Textarea
                  value={manual.registeredAddress}
                  onChange={(e) =>
                    handleManualChange("registeredAddress", e.target.value)
                  }
                  placeholder="Enter registered address"
                  rows={2}
                  data-testid="manual-address"
                />
              </div>

              <div>
                <Label className="mb-2 block">Status</Label>
                <Select
                  value={manual.status}
                  onValueChange={(v) => handleManualChange("status", v)}
                >
                  <SelectTrigger data-testid="manual-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Dormant">Dormant</SelectItem>
                    <SelectItem value="Dissolved">Dissolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">Year End</Label>
                <div className="flex gap-2">
                  <Select
                    value={manual.yearEndDay}
                    onValueChange={(v) => handleManualChange("yearEndDay", v)}
                  >
                    <SelectTrigger className="w-24" data-testid="manual-ye-day">
                      <SelectValue placeholder="Day" />
                    </SelectTrigger>
                    <SelectContent>
                      {DAYS.map((d) => (
                        <SelectItem key={d} value={String(d)}>
                          {d}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select
                    value={manual.yearEndMonth}
                    onValueChange={(v) => handleManualChange("yearEndMonth", v)}
                  >
                    <SelectTrigger
                      className="flex-1"
                      data-testid="manual-ye-month"
                    >
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m, i) => (
                        <SelectItem key={m} value={String(i + 1)}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="col-span-2">
                <Label className="mb-2 block">SIC Codes</Label>
                <Input
                  value={manual.sicCodes}
                  onChange={(e) =>
                    handleManualChange("sicCodes", e.target.value)
                  }
                  placeholder="e.g. 62020, 69201"
                  data-testid="manual-sic"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Comma-separated
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                onClick={handleSaveManual}
                disabled={!manual.name.trim() || saving}
                data-testid="save-manual-btn"
              >
                <PenLine className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Company"}
              </Button>
            </div>
          </TabsContent>

          {/* ── Bulk Upload ── */}
          <TabsContent value="bulk" className="space-y-6">
            <div className="border-2 border-dashed border-border rounded-md p-12 text-center hover:border-primary transition-colors">
              <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-medium mb-2">
                Upload Excel or CSV File
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Upload a file with company names or registration numbers
              </p>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleBulkUpload}
                className="hidden"
                id="bulk-upload"
              />
              <label htmlFor="bulk-upload">
                <Button asChild variant="outline">
                  <span>Choose File</span>
                </Button>
              </label>
              <p className="text-xs text-muted-foreground mt-4">
                <a href="#" className="text-primary underline">
                  Download template
                </a>{" "}
                | Supported: .xlsx, .xls, .csv
              </p>
            </div>
          </TabsContent>
        </Tabs>
    </div>
  );
};

export default AddCompanyPanel;
